import { memo } from "react";

import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { TextField } from "ui/components/TextField/TextField";
import { Toggle } from "ui/components/Toggle";

import { TireAxleGroupProps } from "./types";


/** Prikaz jedne osovine — editabilni red s dropdownima + lista guma ispod */
const TireAxleGroup: React.FC<TireAxleGroupProps> = (props) => {
    const {
        label,
        tireIds,
        tireLabels,
        editState,
        widthOptions,
        heightOptions,
        radiusOptions,
        tireTypeOptions,
        manufacturerOptions,
        dirtyFields,
        onChange,
        onDelete,
    } = props;

    /** Vraća CSS klasu za ćeliju ovisno o tome je li polje promijenjeno */
    const dirtyCell = (field: string) => dirtyFields.has(field) ? "bg-amber-50" : "";

    return (
        <>
            {/* Editabilni red za osovinu */}
            <tr className="border-y border-slate-200 bg-slate-50 text-slate-900">
                <td className="px-4 pt-2 pb-3 text-sm font-semibold text-slate-700" colSpan={2}>
                    {label}
                    <span className="text-xs font-normal text-slate-400 ml-2">({tireIds.length} gume)</span>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("width")}`}>
                    <div className="w-[70px]">
                        <Dropdown
                            name={`axle-width-${tireIds[0]}`}
                            options={widthOptions}
                            value={editState.width}
                            onChange={(_e, _name, item) => onChange("width", item.value)}
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("height")}`}>
                    <div className="w-[70px]">
                        <Dropdown
                            name={`axle-height-${tireIds[0]}`}
                            options={heightOptions}
                            value={editState.height}
                            onChange={(_e, _name, item) => onChange("height", item.value)}
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("radius")}`}>
                    <div className="w-[70px]">
                        <Dropdown
                            name={`axle-radius-${tireIds[0]}`}
                            options={radiusOptions}
                            value={editState.radius}
                            onChange={(_e, _name, item) => onChange("radius", item.value)}
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("tireManufacturerId")}`}>
                    <div className="w-[130px]">
                        <Dropdown
                            name={`axle-manufacturer-${tireIds[0]}`}
                            options={manufacturerOptions}
                            value={editState.tireManufacturerId}
                            onChange={(_e, _name, item) => onChange("tireManufacturerId", item.value)}
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("tireTypeId")}`}>
                    <div className="w-[150px]">
                        <Dropdown
                            name={`axle-tireType-${tireIds[0]}`}
                            options={tireTypeOptions}
                            value={editState.tireTypeId}
                            onChange={(_e, _name, item) => onChange("tireTypeId", item.value)}
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("dot")}`}>
                    <div className="w-[70px]">
                        <TextField
                            type="number"
                            name={`axle-dot-${tireIds[0]}`}
                            value={editState.dot}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onChange("dot", e.target.value ? Number(e.target.value) : null)
                            }
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium ${dirtyCell("profileDepth")}`}>
                    <div className="w-[70px]">
                        <TextField
                            type="number"
                            name={`axle-profileDepth-${tireIds[0]}`}
                            value={editState.profileDepth}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onChange("profileDepth", e.target.value ? Number(e.target.value) : null)
                            }
                            height="small"
                            width="full"
                            cssClassName="bg-white"
                        />
                    </div>
                </td>
                <td className={`px-4 pt-2 pb-2 text-sm font-medium text-center ${dirtyCell("rof")}`}>
                    <Toggle
                        id={`axle-rof-${tireIds[0]}`}
                        name="rof"
                        value={editState.rof}
                        onChange={(_id, _name, value) => onChange("rof", value)}
                    />
                </td>
            </tr>
            {/* Read-only redovi za svaku gumu u grupi */}
            {tireIds.map((id, i) => {
                const manufacturerName = manufacturerOptions.find(o => o.value === editState.tireManufacturerId)?.text ?? "-";
                const tireTypeName = tireTypeOptions.find(o => o.value === editState.tireTypeId)?.text ?? "-";

                return (
                    <tr key={id} className={`${i < tireIds.length - 1 ? "border-b border-slate-200" : ""} hover:bg-slate-50 relative text-slate-900`}>
                        <td className="px-4 py-4 text-sm font-medium text-slate-400">{i + 1}</td>
                        <td className="px-4 py-4 text-sm font-medium">{tireLabels[i]}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("width")}`}>{editState.width ?? "-"}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("height")}`}>{editState.height ?? "-"}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("radius")}`}>{editState.radius ?? "-"}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("tireManufacturerId")}`}>{manufacturerName}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("tireTypeId")}`}>{tireTypeName}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("dot")}`}>{editState.dot ?? "-"}</td>
                        <td className={`px-4 py-4 text-sm font-medium ${dirtyCell("profileDepth")}`}>{editState.profileDepth ?? "-"}</td>
                        <td className={`px-4 py-4 text-sm font-medium text-center ${dirtyCell("rof")}`}>{editState.rof ? "Da" : "Ne"}</td>
                        {/* <td className="px-4 py-2 text-sm font-medium">
                            <div className="flex items-center justify-end">
                                <div
                                    onClick={(e) => onDelete(e, id)}
                                    className="cursor-pointer flex items-center h-[30px]"
                                >
                                    <ReactSVG
                                        src="/media/icons/trash.svg"
                                        className="w-4 h-4 stroke-[#bd0000]"
                                    />
                                </div>
                            </div>
                        </td> */}
                    </tr>
                );
            })}
        </>
    );
};

export default memo(TireAxleGroup);
